import Toast from '@/components/ui/Toast';
import Swal from '@/components/ui/Swal';

/**
 * Notification Service for consistent UI feedback
 * Uses Kanakku template's Toast and Swal components
 */
export const notificationService = {
  /**
   * Success notifications
   */
  success: {
    toast(message, position = 'right', durationMs) {
      Toast('success', message, position, durationMs);
    },
    
    swal(title, message = '') {
      return Swal.fire({
        icon: 'success',
        title: title,
        text: message,
        customClass: {
          confirmButton: 'btn btn-success',
        },
        buttonsStyling: false,
        timer: 3000,
        timerProgressBar: true,
      });
    }
  },

  /**
   * Error notifications
   */
  error: {
    toast(message, position = 'right', durationMs) {
      Toast('error', message, position, durationMs);
    },
    
    swal(title, message = '') {
      return Swal.fire({
        icon: 'error',
        title: title,
        text: message,
        customClass: {
          confirmButton: 'btn btn-danger',
        },
        buttonsStyling: false,
      });
    }
  },

  /**
   * Warning notifications
   */
  warning: {
    toast(message, position = 'right', durationMs) {
      Toast('warning', message, position, durationMs);
    },
    
    swal(title, message = '') {
      return Swal.fire({
        icon: 'warning',
        title: title,
        text: message,
        customClass: {
          confirmButton: 'btn btn-warning',
        },
        buttonsStyling: false,
      });
    }
  },

  /**
   * Info notifications
   */
  info: {
    toast(message, position = 'right', durationMs) {
      Toast('info', message, position, durationMs);
    },
    
    swal(title, message = '') {
      return Swal.fire({
        icon: 'info',
        title: title,
        text: message,
        customClass: {
          confirmButton: 'btn btn-info',
        },
        buttonsStyling: false,
      });
    }
  },

  /**
   * Confirmation dialogs
   */
  confirm: {
    delete(title = 'Are you sure?', text = "You won't be able to revert this!") {
      return Swal.fire({
        title: title,
        text: text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secondary ms-2',
        },
        buttonsStyling: false,
      });
    },

    action(title, text, confirmText = 'Yes, do it!') {
      return Swal.fire({
        title: title,
        text: text,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#0d6efd',
        cancelButtonColor: '#6c757d',
        confirmButtonText: confirmText,
        cancelButtonText: 'Cancel',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-secondary ms-2',
        },
        buttonsStyling: false,
      });
    }
  },

  /**
   * Loading states
   */
  loading: {
    show(title = 'Processing...', text = 'Please wait while we process your request.') {
      return Swal.fire({
        title: title,
        text: text,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        }
      });
    },

    close() {
      Swal.close();
    }
  },

  /**
   * Validation error handling
   */
  validationErrors(errors) {
    if (typeof errors === 'object' && errors !== null) {
      const errorMessages = Object.values(errors)
        .flat()
        .join('\n');
      
      this.error.swal('Validation Error', errorMessages);
    } else if (typeof errors === 'string') {
      this.error.toast(errors);
    }
  },

  /**
   * Network error handling
   */
  networkError(error) {
    if (error.status === 0) {
      this.error.swal(
        'Connection Error', 
        'Unable to connect to the server. Please check your internet connection.'
      );
    } else if (error.status >= 500) {
      this.error.swal(
        'Server Error', 
        'Something went wrong on our end. Please try again later.'
      );
    } else {
      this.error.toast(error.message || 'An unexpected error occurred.');
    }
  }
};

export default notificationService;
